/*
 * Lookbook grid: opens each look in a native <dialog>, lets the customer pick a size per piece
 * and add one piece or the whole look to the cart drawer.
 *
 * Buy controls only exist in the markup when the section's "enable_product_links" setting is on
 * (pre-launch lock). This script never creates them on its own.
 */
if (!customElements.get('lookbook-grid')) {
  class LookbookGrid extends HTMLElement {
    connectedCallback() {
      this.onClick = this.handleClick.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onClose = this.handleClose.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      // "close" does not bubble, so listen in the capture phase.
      this.addEventListener('close', this.onClose, true);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('close', this.onClose, true);
      document.body.classList.remove('lookbook-grid-modal-open');
    }

    handleClick(event) {
      const target = event.target;

      if (target instanceof HTMLDialogElement) {
        // A click that lands on the <dialog> itself is a click on the backdrop.
        target.close();
        return;
      }

      const opener = target.closest('[data-lookbook-open]');
      if (opener) {
        this.openDialog(document.getElementById(opener.dataset.lookbookOpen));
        return;
      }

      const closer = target.closest('[data-lookbook-close]');
      if (closer) {
        closer.closest('dialog')?.close();
        return;
      }

      const addButton = target.closest('[data-lookbook-add]');
      if (addButton) {
        this.addPieces([addButton.closest('.lookbook-piece')], addButton);
        return;
      }

      const addAllButton = target.closest('[data-lookbook-add-all]');
      if (addAllButton) {
        const dialog = addAllButton.closest('dialog');
        this.addPieces([...dialog.querySelectorAll('[data-lookbook-piece]')], addAllButton, true);
        return;
      }

      if (target.closest('[data-lookbook-size-guide]')) {
        this.openDialog(this.querySelector('[data-lookbook-size-guide-dialog]'));
      }
    }

    handleChange(event) {
      if (!event.target.matches('.lookbook-size__input')) return;

      const piece = event.target.closest('.lookbook-piece');
      this.setPieceError(piece, '');

      const dialog = event.target.closest('dialog');
      this.setBundleError(dialog, '');
      this.updateTotal(dialog);
    }

    handleClose(event) {
      event.target.querySelectorAll?.('video').forEach((video) => video.pause());
      if (!this.querySelector('dialog[open]')) {
        document.body.classList.remove('lookbook-grid-modal-open');
      }
    }

    openDialog(dialog) {
      if (!dialog || typeof dialog.showModal !== 'function') return;

      this.hydrate(dialog);
      if (!dialog.open) dialog.showModal();
      dialog.querySelectorAll('video').forEach((video) => video.play()?.catch(() => {}));
      document.body.classList.add('lookbook-grid-modal-open');
    }

    hydrate(dialog) {
      const template = dialog.querySelector(':scope > template[data-lookbook-template]');
      if (!template) return;

      const content = template.content.cloneNode(true);
      const bundle = content.querySelector('[data-lookbook-bundle]');
      if (bundle) bundle.hidden = !content.querySelector('[data-lookbook-piece]');

      template.replaceWith(content);
      this.updateTotal(dialog);
    }

    getSelectedVariant(piece) {
      const fixed = piece.querySelector('[data-lookbook-default-variant]');
      if (fixed) return { id: Number(fixed.value), price: Number(piece.dataset.price) };

      const checked = piece.querySelector('.lookbook-size__input:checked');
      if (!checked) return null;
      return { id: Number(checked.value), price: Number(checked.dataset.price) };
    }

    updateTotal(dialog) {
      const totalElement = dialog?.querySelector('[data-lookbook-total]');
      if (!totalElement) return;

      const total = [...dialog.querySelectorAll('[data-lookbook-piece]')].reduce((sum, piece) => {
        const selected = this.getSelectedVariant(piece);
        return sum + (selected ? selected.price : Number(piece.dataset.price) || 0);
      }, 0);

      totalElement.textContent = this.formatPrice(total);
    }

    formatPrice(cents) {
      const format = window.theme?.settings?.money_with_currency_format;
      if (typeof window.formatMoney === 'function' && format) return window.formatMoney(cents, format);
      return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    setPieceError(piece, message) {
      const error = piece?.querySelector('[data-lookbook-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
      piece.classList.toggle('has-error', Boolean(message));
    }

    setBundleError(dialog, message) {
      const error = dialog?.querySelector('[data-lookbook-bundle-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }

    async addPieces(pieces, button, isBundle = false) {
      const dialog = button.closest('dialog');
      const items = [];
      const missing = [];

      pieces.forEach((piece) => {
        const selected = this.getSelectedVariant(piece);
        if (selected) {
          items.push({ id: selected.id, quantity: 1 });
          this.setPieceError(piece, '');
        } else {
          missing.push(piece);
          this.setPieceError(piece, this.dataset.sizeError);
        }
      });

      if (missing.length) {
        if (isBundle) this.setBundleError(dialog, this.dataset.bundleError);
        missing[0].querySelector('.lookbook-size__input:not(:disabled)')?.focus();
        return;
      }

      if (!items.length || this.isAdding) return;

      const drawerElement = document.querySelector('cart-drawer');
      const cartDrawer = typeof drawerElement?.getSectionsToRender === 'function' ? drawerElement : null;
      const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((section) => section.id) : [];
      const buyButtons = dialog.querySelectorAll('[data-lookbook-add], [data-lookbook-add-all]');

      this.isAdding = true;
      button.setAttribute('aria-busy', 'true');
      buyButtons.forEach((buyButton) => (buyButton.disabled = true));
      this.setBundleError(dialog, '');

      try {
        const response = await fetch(window.routes.cart_add_url, {
          ...fetchConfig('javascript'),
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        const state = await response.json();

        if (state.status) {
          this.showAddError(pieces, dialog, isBundle, state.description || state.message || this.dataset.cartError);
          // A rejected multi-item request may still have added part of the look; keep the drawer in sync.
          this.refreshCart(cartDrawer);
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lookbook-grid', cartData: state });
        }
        document.dispatchEvent(new CustomEvent('cartUpdated', { detail: { message: 'Cart was updated' } }));

        if (!cartDrawer) {
          // Store set to cart page instead of drawer.
          window.location.href = window.routes.cart_url;
          return;
        }

        // The drawer cannot appear above a modal <dialog>, so close the look first.
        dialog?.close();
        // Same as product-form.js: an empty drawer hides its item list until this class goes away.
        cartDrawer.classList.remove('is-empty');
        if (state.sections) {
          cartDrawer.renderContents(state);
        } else {
          await this.refreshCart(cartDrawer);
          cartDrawer.open();
        }
      } catch (error) {
        console.error('[lookbook-grid] add to cart failed', error);
        this.showAddError(pieces, dialog, isBundle, this.dataset.cartError);
      } finally {
        this.isAdding = false;
        button.removeAttribute('aria-busy');
        buyButtons.forEach((buyButton) => (buyButton.disabled = false));
      }
    }

    showAddError(pieces, dialog, isBundle, message) {
      if (isBundle) {
        this.setBundleError(dialog, message);
      } else {
        this.setPieceError(pieces[0], message);
      }
    }

    async refreshCart(cartDrawer) {
      if (!cartDrawer) return;

      const sections = cartDrawer.getSectionsToRender();
      try {
        const response = await fetch(`${window.routes.cart_url}?sections=${sections.map((s) => s.id).join(',')}`);
        const html = await response.json();

        sections.forEach((section) => {
          const element = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
          if (element && html[section.id]) {
            element.innerHTML = cartDrawer.getSectionInnerHTML(html[section.id], section.selector);
          }
        });
        cartDrawer.classList.toggle('is-empty', !cartDrawer.querySelector('.cart-item'));
        document.dispatchEvent(new CustomEvent('cartUpdated', { detail: { message: 'Cart was updated' } }));
      } catch (error) {
        console.error('[lookbook-grid] cart refresh failed', error);
      }
    }
  }

  customElements.define('lookbook-grid', LookbookGrid);
}
